import { useEffect, useState } from 'react';
import { collection, limit, onSnapshot, query, where } from 'firebase/firestore';
import { db } from './config';
import type { Mark } from '../quran/mistakes';

/**
 * The corrections a teacher has marked on one pupil — م-3-02's "أين تكرر
 * خطؤه". Readable by the pupil, the teacher who marked, a guardian and the
 * class teacher (firestore.rules, match /marks); nobody writes them but
 * markError on the server.
 *
 * Capped: a guardian reads where mistakes cluster, not an audit log, and a
 * pupil who has been corrected hundreds of times has a pattern well before
 * the cap.
 */
const MAX_MARKS = 300;

export function useStudentMarks(studentId: string | null): { marks: Mark[]; loading: boolean } {
  const [state, setState] = useState<{ marks: Mark[]; loading: boolean }>({ marks: [], loading: true });
  useEffect(() => {
    if (!studentId) { setState({ marks: [], loading: false }); return undefined; }
    setState({ marks: [], loading: true });
    return onSnapshot(
      query(collection(db, 'marks'), where('studentId', '==', studentId), limit(MAX_MARKS)),
      (snap) => setState({
        marks: snap.docs.map((d) => {
          const x = d.data();
          return {
            surah: Number(x.surah), ayah: Number(x.ayah), word: Number(x.word), at: Number(x.at ?? 0),
          };
        }),
        loading: false,
      }),
      // Refused or offline reads the same as "none yet": a guardian is not
      // shown a broken card for a child who simply has no corrections.
      () => setState({ marks: [], loading: false }),
    );
  }, [studentId]);
  return state;
}
